export const ANALYSIS_WINDOW_ALL = 240; // ~2 sec if ~120 subchunks/sec (roughly)
export const ANALYSIS_WINDOW_SILENT = 240; // same cap
export const NOISE_CHECK_INTERVAL_MS = 1000;

// Thresholds (tune after a few sessions)
export const SILENCE_DBFS = -48; // below this is considered "silent" for noise-floor measurement
export const NOISE_FLOOR_WARN_DBFS = -30; // if noise floor higher than this => noisy environment (raised to reduce false warnings on quiet room noise)
// When there are no true silent frames (room never drops below SILENCE_DBFS), require an
// even higher lower-percentile level before warning. Avoids treating headset speech peaks
// as ambient noise (old avg/max checks did that).
export const CONTINUOUS_NOISE_WARN_DBFS = -25;
// export const MIN_SPEECH_DBFS = -35; // require speech to be at least this loud sometimes
export const SNR_WARN_DB = 10; // warn if SNR below this (voice not distinct)
export const WARN_COOLDOWN_MS = 12000; // don't spam warnings

// VAD tuning: dynamic silence threshold = max(SILENCE_DBFS, noiseFloor + margin), clamped below speech floor.
export const VAD_NOISE_MARGIN_DB = 4;
// export const VAD_SILENCE_DBFS_MAX = MIN_SPEECH_DBFS - 2; // don't treat very quiet speech as silence
export const VAD_SILENCE_DBFS_MAX = -37; // don't treat very quiet speech as silence (temporary value while MIN_SPEECH_DBFS is commented)

// ---------- Fast audio metrics (no allocations) ----------
const EPS = 1e-12;

export function rmsAndDbfsInt16(chunk: Int16Array): {
  rms: number;
  dbfs: number;
} {
  if (!chunk || chunk.length === 0) return { rms: 0, dbfs: -Infinity };

  let sumSq = 0;
  for (let i = 0; i < chunk.length; i++) {
    const x = chunk[i] / 32768; // normalize to [-1, 1)
    sumSq += x * x;
  }
  const rms = Math.sqrt(sumSq / chunk.length);
  const dbfs = 20 * Math.log10(rms + EPS);
  return { rms, dbfs };
}

function percentile(values: number[], p: number): number {
  // p in [0..100]
  if (!values.length) return -Infinity;
  const arr = values.slice().sort((a, b) => a - b);
  const idx = (p / 100) * (arr.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return arr[lo];
  const t = idx - lo;
  return arr[lo] * (1 - t) + arr[hi] * t;
}

function pushCapped(arr: number[], v: number, cap: number) {
  arr.push(v);
  if (arr.length > cap) arr.shift();
}

export interface NoiseAnalysisResult {
  shouldWarn: boolean;
  message: string | null;
  noiseFloorDb: number;
  speechLevelDb: number;
  snrDb: number;
}

export class NoiseAnalyzer {
  private allDbfsWindow: number[] = [];
  private silentDbfsWindow: number[] = [];
  private lastNoiseCheckTs: number = 0;
  private lastNoiseWarnTs: number = 0;
  private noiseWarningShown: boolean = false;
  private lastNoiseFloorDb: number = -Infinity;
  private lastSpeechLevelDb: number = -Infinity;
  private lastSnrDb: number = -Infinity;

  /**
   * Process an audio chunk and return noise analysis result if check interval has passed
   */
  processChunk(
    chunk: Int16Array,
    isProcessingPaused: boolean
  ): NoiseAnalysisResult | null {
    // --- Metrics for this subChunk (fast) ---
    const { dbfs } = rmsAndDbfsInt16(chunk);

    // --- VAD-lite: treat as silent if below SILENCE_DBFS ---
    const isSilent = dbfs < SILENCE_DBFS;

    // --- Maintain rolling windows ---
    pushCapped(this.allDbfsWindow, dbfs, ANALYSIS_WINDOW_ALL);
    if (isSilent) {
      pushCapped(this.silentDbfsWindow, dbfs, ANALYSIS_WINDOW_SILENT);
    }

    // --- Run noise check ~ once per second (cheap + stable) ---
    const now = Date.now();
    if (now - this.lastNoiseCheckTs >= NOISE_CHECK_INTERVAL_MS) {
      this.lastNoiseCheckTs = now;

      // Need enough samples (avoid early false triggers)
      const allN = this.allDbfsWindow.length;
      const silentN = this.silentDbfsWindow.length;

      if (allN >= 80 && !isProcessingPaused) {
        // Background noise estimate:
        // - Prefer silent frames median when we have enough true silence samples
        // - Otherwise fall back to a lower percentile of all frames (for VAD +
        //   continuous-noise detection when the room never goes fully silent)
        const hasReliableNoiseFloor = silentN >= 10;
        const noiseFloorDb = hasReliableNoiseFloor
          ? percentile(this.silentDbfsWindow, 50)
          : percentile(this.allDbfsWindow, 20);

        // Speech estimate from overall frames (upper percentile)
        // const speechLevelDb = percentile(this.allDbfsWindow, 80);
        // const snrDb = speechLevelDb - noiseFloorDb;
        const speechLevelDb = -Infinity; // Temporarily disabled
        const snrDb = -Infinity; // Temporarily disabled

        this.lastNoiseFloorDb = noiseFloorDb;
        this.lastSpeechLevelDb = speechLevelDb;
        this.lastSnrDb = snrDb;

        // Optional: require that user actually spoke at least a bit
        // const hadSpeech = speechLevelDb > MIN_SPEECH_DBFS;
        // const hadSpeech = false; // Temporarily disabled

        // Continuous loud ambient (no true silence): use the quieter end of the
        // window (p10), not avg/max. Speech (esp. headset) has high peaks but
        // valleys; steady room noise keeps even the quiet moments elevated.
        const lowerLevelDb = percentile(this.allDbfsWindow, 10);
        const isConsistentlyLoud =
          !hasReliableNoiseFloor &&
          lowerLevelDb > CONTINUOUS_NOISE_WARN_DBFS;

        const shouldWarn =
          (hasReliableNoiseFloor && noiseFloorDb > NOISE_FLOOR_WARN_DBFS) ||
          // (hadSpeech && snrDb < SNR_WARN_DB) || // Temporarily disabled
          isConsistentlyLoud;

        const cooldownOk = now - this.lastNoiseWarnTs > WARN_COOLDOWN_MS;

        if (shouldWarn && cooldownOk && !this.noiseWarningShown) {
          this.lastNoiseWarnTs = now;
          this.noiseWarningShown = true;

          let message =
            "Background noise seems high. Please move to a quieter place or use a headset.";
          // if (hadSpeech && snrDb < SNR_WARN_DB) { // Temporarily disabled
          //   message =
          //     "Your voice is hard to distinguish from background noise. Please use a headset or move to a quieter place.";
          // } else
          if (isConsistentlyLoud) {
            message =
              "Environment seems noisy. Please move to a quieter place or use a headset.";
          }

          return {
            shouldWarn: true,
            message,
            noiseFloorDb,
            speechLevelDb,
            snrDb,
          };
        }

        // If conditions improved, allow warning to show again later
        // (so it can re-trigger after cooldown when noise returns)
        if (!shouldWarn) {
          this.noiseWarningShown = false;
        }
      }
    }

    return null;
  }

  /**
   * Dynamic "silence" threshold for VAD: adapts to background noise so end-of-utterance
   * is detected reliably even in non-quiet environments.
   */
  getVadSilenceDbfs(): number {
    const nf = this.lastNoiseFloorDb;
    const dynamic =
      Number.isFinite(nf) && nf !== -Infinity
        ? Math.max(SILENCE_DBFS, nf + VAD_NOISE_MARGIN_DB)
        : SILENCE_DBFS;

    // Do not let the silence threshold get too high (avoid clipping quiet speech).
    return Math.min(dynamic, VAD_SILENCE_DBFS_MAX);
  }

  /**
   * Reset all analysis state
   */
  reset(): void {
    this.allDbfsWindow = [];
    this.silentDbfsWindow = [];
    this.lastNoiseCheckTs = 0;
    this.lastNoiseWarnTs = 0;
    this.noiseWarningShown = false;
    this.lastNoiseFloorDb = -Infinity;
    this.lastSpeechLevelDb = -Infinity;
    this.lastSnrDb = -Infinity;
  }

  /**
   * Clear the warning flag (allows warning to show again after cooldown)
   */
  clearWarning(): void {
    this.noiseWarningShown = false;
  }

  /**
   * Get current state (for debugging)
   */
  getState() {
    return {
      allDbfsWindowLength: this.allDbfsWindow.length,
      silentDbfsWindowLength: this.silentDbfsWindow.length,
      lastNoiseCheckTs: this.lastNoiseCheckTs,
      lastNoiseWarnTs: this.lastNoiseWarnTs,
      noiseWarningShown: this.noiseWarningShown,
      lastNoiseFloorDb: this.lastNoiseFloorDb,
      lastSpeechLevelDb: this.lastSpeechLevelDb,
      lastSnrDb: this.lastSnrDb,
      vadSilenceDbfs: this.getVadSilenceDbfs(),
    };
  }
}
